import { z } from "zod";

import {
  CapacidadDocente,
  EstadoExpediente,
  EstadoSolicitud,
  EstadoUsuario,
  ModalidadSustentacion,
  ResultadoRevisionBorrador,
  ResultadoRevisionProyecto,
  Rol,
  RolJurado,
  TipoDocumento,
} from "./enums";

/**
 * Helper para convertir un objeto `const as const` en un z.enum de Zod
 * tipado correctamente. Asume que el objeto tiene al menos un valor,
 * lo cual está garantizado por convención en este proyecto (ver enums.ts).
 */
const enumValues = <T extends Record<string, string>>(obj: T): [T[keyof T], ...T[keyof T][]] =>
  Object.values(obj) as [T[keyof T], ...T[keyof T][]];

// ============================================================================
// Schemas de enums
// ============================================================================

export const rolSchema = z.enum(enumValues(Rol));
export const estadoUsuarioSchema = z.enum(enumValues(EstadoUsuario));
export const tipoDocumentoSchema = z.enum(enumValues(TipoDocumento));
export const rolJuradoSchema = z.enum(enumValues(RolJurado));
export const modalidadSustentacionSchema = z.enum(enumValues(ModalidadSustentacion));
export const resultadoRevisionProyectoSchema = z.enum(enumValues(ResultadoRevisionProyecto));
export const resultadoRevisionBorradorSchema = z.enum(enumValues(ResultadoRevisionBorrador));

// ============================================================================
// Schemas comunes
// ============================================================================

export const uuidSchema = z.string().uuid();
export const idempotencyKeySchema = z
  .string()
  .uuid({ message: "idempotency_key debe ser UUID v4" });

export const paginacionSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  sort: z.string().optional(),
});
export type PaginacionInput = z.infer<typeof paginacionSchema>;

// ============================================================================
// Auth
// ============================================================================

export const loginSchema = z.object({
  correo: z.string().email("Correo inválido"),
  password: z.string().min(8, "Mínimo 8 caracteres"),
});
export type LoginInput = z.infer<typeof loginSchema>;

/**
 * Roles permitidos en el AUTO-REGISTRO público (`POST /auth/register`). SÓLO los
 * de auto-servicio (estudiante / bachiller). Los roles privilegiados (ASESOR,
 * JURADO, DECANATO, REPOSITORIO, ADMIN, SUPER_ADMIN) los provisiona un actor
 * autorizado (ADR 053), NUNCA por auto-registro: antes el endpoint público
 * aceptaba el enum COMPLETO (`rolSchema`), lo que permitía a cualquiera
 * registrarse como ADMIN/SUPER_ADMIN — escalada de privilegios.
 */
export const ROLES_REGISTRO_PUBLICO = [Rol.ESTUDIANTE, Rol.BACHILLER] as const;
export const rolRegistroPublicoSchema = z.enum([Rol.ESTUDIANTE, Rol.BACHILLER]);

export const registerSchema = z.object({
  nombres: z.string().min(2),
  apellidos: z.string().min(2),
  documentoIdentidad: z.string().min(8).max(15),
  correo: z.string().email(),
  telefono: z.string().optional(),
  password: z.string().min(8),
  rol: rolRegistroPublicoSchema,
});
export type RegisterInput = z.infer<typeof registerSchema>;

// ============================================================================
// Expedientes — listar y crear
// ============================================================================

export const estadoExpedienteSchema = z.enum(enumValues(EstadoExpediente));

export const listarExpedientesQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  estado: estadoExpedienteSchema.optional(),
  escuelaId: uuidSchema.optional(),
  busqueda: z.string().trim().min(1).max(200).optional(),
});
export type ListarExpedientesQueryInput = z.infer<typeof listarExpedientesQuerySchema>;

/**
 * Crear expediente de tesis. La escuela/facultad **no** viaja en el wire:
 * el backend la deriva de la matrícula del estudiante (`PerfilEstudiante`),
 * que es la fuente única de verdad (ADR 035). El estudiante solo propone el
 * título; su escuela queda como snapshot derivado en `ExpedienteTesis`.
 */
export const crearExpedienteSchema = z.object({
  tituloPropuesto: z.string().min(10, "El título debe tener al menos 10 caracteres").max(500),
  /**
   * Tipo de tesis (ADR 056): la plantilla de PROYECTO que el estudiante elige al
   * iniciar el expediente (cuantitativa / cualitativa / proyectual). Queda fijada
   * desde el inicio y predetermina el proyecto LaTeX. El backend valida que exista,
   * esté activa y sea de modalidad PROYECTO.
   */
  plantillaProyectoId: uuidSchema,
});
export type CrearExpedienteInput = z.infer<typeof crearExpedienteSchema>;

// ============================================================================
// Academic — filtros de catálogo
// ============================================================================

export const filtroPorFacultadSchema = z.object({
  facultadId: uuidSchema.optional(),
});
export type FiltroPorFacultadInput = z.infer<typeof filtroPorFacultadSchema>;

// ============================================================================
// Users — admin
// ============================================================================

export const listarUsuariosQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  rol: rolSchema.optional(),
  estado: estadoUsuarioSchema.optional(),
  busqueda: z.string().trim().min(1).max(100).optional(),
});
export type ListarUsuariosQueryInput = z.infer<typeof listarUsuariosQuerySchema>;

export const cambiarEstadoUsuarioSchema = z.object({
  estado: estadoUsuarioSchema,
});
export type CambiarEstadoUsuarioInput = z.infer<typeof cambiarEstadoUsuarioSchema>;

/**
 * Otorgar/revocar capacidades de un docente (ADR 054). Lo usa el ADMIN sobre un
 * docente de su institución. Ambos flags se envían siempre (estado completo,
 * idempotente): el front manda el estado deseado de los dos toggles.
 */
export const actualizarCapacidadesDocenteSchema = z.object({
  puedeAsesorar: z.boolean(),
  puedeSerJurado: z.boolean(),
});
export type ActualizarCapacidadesDocenteInput = z.infer<typeof actualizarCapacidadesDocenteSchema>;

/**
 * Filtro de búsqueda de docentes elegibles (ADR 054). Se filtra por CAPACIDAD
 * (`puedeAsesorar`/`puedeSerJurado`), no por rol: el combobox de designación de
 * asesor pide `capacidad=asesor` y el de jurado `capacidad=jurado`.
 * Accesible a estudiantes y bachilleres para elegir asesor propuesto.
 */
export const buscarDocentesQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(20),
  busqueda: z.string().trim().min(1).max(100).optional(),
  capacidad: z.enum([CapacidadDocente.ASESOR, CapacidadDocente.JURADO]).optional(),
  // ADR 055 (enmienda) — acotar candidatos por la EAP del estudiante (reemplaza departamentoId).
  escuelaId: uuidSchema.optional(),
  // ADR 055 — incluir docentes aún no activados (INACTIVO). Sólo lo honra el backend
  // para el ADMIN (panel de gestión); la búsqueda de asesor/jurado ignora el flag y
  // siempre lista sólo ACTIVOs (no se puede designar a quien aún no puede actuar).
  // Parseo por texto (no z.coerce.boolean: "false" sería truthy): sólo "true" ⇒ true.
  incluirInactivos: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((v) => v === true || v === "true"),
});
export type BuscarDocentesQueryInput = z.infer<typeof buscarDocentesQuerySchema>;

/**
 * Filtro de listado de estudiantes para el panel del ADMIN (ADR 055). Espejo de
 * `buscarDocentesQuerySchema` sin `capacidad` (los estudiantes no tienen
 * capacidades). El endpoint es ADMIN-only y tenant-scoped; el panel pasa
 * `incluirInactivos=true` para ver también a los invitados que aún no activan.
 */
export const buscarEstudiantesQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(20),
  busqueda: z.string().trim().min(1).max(100).optional(),
  // Acotar a una EAP (escuela) concreta del tenant.
  escuelaId: uuidSchema.optional(),
  // Mismo parseo por texto que docentes (no z.coerce.boolean: "false" sería truthy).
  incluirInactivos: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((v) => v === true || v === "true"),
});
export type BuscarEstudiantesQueryInput = z.infer<typeof buscarEstudiantesQuerySchema>;

export const cambiarPasswordSchema = z
  .object({
    passwordActual: z.string().min(8),
    passwordNuevo: z.string().min(8),
    passwordConfirmacion: z.string().min(8),
  })
  .refine((data) => data.passwordNuevo === data.passwordConfirmacion, {
    message: "Las contraseñas no coinciden",
    path: ["passwordConfirmacion"],
  });
export type CambiarPasswordInput = z.infer<typeof cambiarPasswordSchema>;

// ============================================================================
// Aceptación de asesor (Fase 6 — ADR 008)
// ============================================================================

/**
 * Propuesta de asesoría que el estudiante envía al asesor antes de
 * presentar la solicitud formal al Decanato. El asesor responde
 * dentro del sistema; si acepta, el sistema genera la
 * CARTA_ACEPTACION_ASESOR firmada con QR.
 */
export const crearAceptacionAsesorSchema = z.object({
  expedienteId: uuidSchema,
  asesorPropuestoId: uuidSchema,
  tituloPropuesto: z
    .string()
    .trim()
    .min(20, "El título debe tener al menos 20 caracteres")
    .max(300, "El título no puede exceder 300 caracteres"),
  resumen: z
    .string()
    .trim()
    .min(50, "El resumen debe tener al menos 50 caracteres")
    .max(3000, "El resumen no puede exceder 3000 caracteres"),
  lineaInvestigacion: z.string().trim().max(200).optional(),
});
export type CrearAceptacionAsesorInput = z.infer<typeof crearAceptacionAsesorSchema>;

export const rechazarAceptacionAsesorSchema = z.object({
  motivoRechazo: z.string().trim().min(20, "El motivo debe tener al menos 20 caracteres").max(1000),
});
export type RechazarAceptacionAsesorInput = z.infer<typeof rechazarAceptacionAsesorSchema>;

// ============================================================================
// Solicitud de asesor
// ============================================================================

// `crearSolicitudAsesorSchema` se eliminó tras ADR 036: la solicitud al Decanato
// ya no se crea con un POST propio — la presenta automáticamente la aceptación
// del asesor (`AceptacionAsesorService.aceptar` → `presentarTrasAceptacion`).
// El ciclo de revisión decanal (observar/subsanar) conserva sus schemas.

export const observarSolicitudSchema = z.object({
  observaciones: z
    .string()
    .min(10, "Las observaciones deben tener al menos 10 caracteres")
    .max(2000),
});
export type ObservarSolicitudInput = z.infer<typeof observarSolicitudSchema>;

export const subsanarSolicitudSchema = z.object({
  resumen: z.string().min(50).max(2000).optional(),
});
export type SubsanarSolicitudInput = z.infer<typeof subsanarSolicitudSchema>;

export const estadoSolicitudSchema = z.enum(enumValues(EstadoSolicitud));

export const listarSolicitudesQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  estado: estadoSolicitudSchema.optional(),
});
export type ListarSolicitudesQueryInput = z.infer<typeof listarSolicitudesQuerySchema>;

// ============================================================================
// Designación de asesor
// ============================================================================

export const designarAsesorSchema = z.object({
  asesorId: uuidSchema,
  numeroResolucion: z
    .string()
    .min(1)
    .regex(
      /^[A-Z0-9-]+$/,
      "El número de resolución debe contener solo mayúsculas, dígitos y guiones",
    ),
  fechaResolucion: z.coerce.date(),
});
export type DesignarAsesorInput = z.infer<typeof designarAsesorSchema>;

/**
 * Input para que el asesor emita su informe de conformidad del proyecto.
 *
 * **Cambio en ADR 015 — Bloque 2:** se eliminó `documentoConformidadId`.
 * Antes el asesor debía subir un PDF firmado a mano; ahora el sistema
 * genera el `INFORME_CONFORMIDAD_ASESOR` con QR + hash desde este endpoint,
 * usando `observaciones` (opcional) y la compilación LaTeX como input
 * estructurado. La emisión del PDF ocurre dentro de la transacción del
 * `AsesoriaService.emitirConformidad`.
 */
export const emitirConformidadProyectoSchema = z.object({
  observaciones: z.string().min(10).max(2000).optional(),
  /**
   * Compilación LaTeX EXITOSA cuyo PDF el asesor está aprobando.
   * Opcional para no romper expedientes legacy sin proyecto LaTeX (donde
   * la conformidad se emite sobre el proyecto sin vínculo criptográfico).
   * Cuando se provee, el sistema vincula el `pdfHashSha256` de esa
   * compilación al informe (ADR 012).
   */
  compilacionLatexId: uuidSchema.optional(),
});
export type EmitirConformidadProyectoInput = z.infer<typeof emitirConformidadProyectoSchema>;

/**
 * Schema del POST `/expedientes/:id/observar-proyecto` (ADR 019).
 * El asesor devuelve el expediente al estudiante con observaciones
 * obligatorias. A diferencia de `emitirConformidad`, acá las observaciones
 * son OBLIGATORIAS — no tiene sentido "observar sin motivo".
 */
export const observarProyectoSchema = z.object({
  observaciones: z
    .string()
    .min(10, "Las observaciones deben tener al menos 10 caracteres")
    .max(2000, "Las observaciones no pueden exceder 2000 caracteres"),
});
export type ObservarProyectoInput = z.infer<typeof observarProyectoSchema>;

// ============================================================================
// Designación de jurado (ADR 028 — alineado al reglamento UNHEVAL 2025)
// ============================================================================

const miembroJuradoSchema = z.object({
  docenteId: uuidSchema,
  rolJurado: rolJuradoSchema,
  esTitular: z.boolean(),
});

/**
 * Designación del Jurado Evaluador (ADR 028, revierte ADR 027).
 *
 * Composición fija según reglamento UNHEVAL 2025 (REG-M.02-002-VRA):
 *  - Art. 32°a: "tres (03) docentes ordinarios como titulares y dos (02)
 *    docentes ordinarios como accesitario, de los cuales dos docentes
 *    titulares deben ser de la especialidad."
 *  - Disp. Compl. SEXTA: los accesitarios "completarán el respectivo
 *    Jurado Evaluador cuando no asiste alguno de sus miembros titulares".
 *
 * Es decir: **5 miembros = 3 titulares (PRESIDENTE / SECRETARIO / VOCAL)
 * + 2 accesitarios suplentes**. Una sola designación por expediente; el
 * mismo jurado dictamina el proyecto, el borrador/informe y participa en
 * la sustentación (no se re-designa entre etapas — ver ADR 028 §1.2).
 */
export const designarJuradoSchema = z
  .object({
    miembros: z.array(miembroJuradoSchema).length(5, "El jurado debe tener exactamente 5 miembros"),
    numeroResolucion: z.string().min(1),
    fechaResolucion: z.coerce.date(),
  })
  .refine(
    (data) => {
      const titulares = data.miembros.filter((m) => m.esTitular);
      const accesitarios = data.miembros.filter((m) => !m.esTitular);
      return titulares.length === 3 && accesitarios.length === 2;
    },
    { message: "Deben ser 3 titulares y 2 accesitarios", path: ["miembros"] },
  )
  .refine(
    (data) => {
      const rolesTitulares = data.miembros.filter((m) => m.esTitular).map((m) => m.rolJurado);
      return (
        rolesTitulares.includes(RolJurado.PRESIDENTE) &&
        rolesTitulares.includes(RolJurado.SECRETARIO) &&
        rolesTitulares.includes(RolJurado.VOCAL)
      );
    },
    {
      message: "Los titulares deben cubrir los roles PRESIDENTE, SECRETARIO y VOCAL",
      path: ["miembros"],
    },
  )
  .refine(
    (data) => {
      const docentes = data.miembros.map((m) => m.docenteId);
      return new Set(docentes).size === docentes.length;
    },
    { message: "No se puede repetir el mismo docente en el jurado", path: ["miembros"] },
  );
export type DesignarJuradoInput = z.infer<typeof designarJuradoSchema>;

// ============================================================================
// Informe colegiado del jurado
// ============================================================================

/**
 * Input para que un miembro titular del jurado emita SU informe individual
 * dentro del ciclo de revisión del proyecto. Cuando el 3er titular emite,
 * el sistema genera UN documento `INFORME_COLEGIADO` consolidado que agrega
 * las 3 observaciones (ADR 015 — Bloque 3). Antes de B3 cada titular subía
 * su PDF firmado y el sistema solo lo registraba; ahora la firma colegiada
 * proviene del propio sistema con QR + hash.
 */
export const emitirInformeColegiadoSchema = z.object({
  // El resultado ya está restringido a los 3 valores válidos en el enum
  // (PENDIENTE no se persiste; ver enums.ts).
  resultado: resultadoRevisionProyectoSchema,
  observaciones: z.string().max(4000).optional(),
});
export type EmitirInformeColegiadoInput = z.infer<typeof emitirInformeColegiadoSchema>;

// ============================================================================
// Corrección de proyecto observado (estudiante)
// ============================================================================

/**
 * Input de corrección del proyecto observado por el estudiante.
 *
 * **ADR 015 — Bloque 7**: el campo `documentoConformidadId` se eliminó. La
 * renovación de la conformidad la emite el asesor por separado (vía
 * `POST /expedientes/:id/conformidad-proyecto`, válido en estado
 * `PROYECTO_OBSERVADO`); el service verifica que existe una conformidad
 * `VIGENTE` posterior a la última observación antes de aceptar la corrección.
 */
export const corregirProyectoSchema = z.object({
  /** ID del nuevo documento PROYECTO_TESIS versionado. */
  documentoProyectoId: uuidSchema,
});
export type CorregirProyectoInput = z.infer<typeof corregirProyectoSchema>;

// ============================================================================
// Presentación del borrador de tesis (estudiante)
// ============================================================================

/**
 * Input de presentación del borrador (estudiante).
 *
 * **ADR 015 — Bloque 7**: el campo `documentoConformidadAsesorId` se eliminó.
 * El asesor debe haber emitido una nueva conformidad sobre el borrador (vía
 * `POST /expedientes/:id/conformidad-proyecto`, válido en estado
 * `TESIS_EN_EJECUCION`) **antes** de que el estudiante presente. El service
 * verifica que existe una conformidad `VIGENTE` posterior a
 * `fechaAprobacionProyecto`.
 */
export const presentarBorradorSchema = z.object({
  /** Documento BORRADOR_TESIS subido por el estudiante. */
  documentoBorradorId: uuidSchema,
});
export type PresentarBorradorInput = z.infer<typeof presentarBorradorSchema>;

// ============================================================================
// Dictamen de borrador (miembro titular del jurado)
// ============================================================================

/**
 * Input del dictamen de suficiencia que emite cada titular del jurado sobre
 * el borrador. ADR 015 — Bloque 4: el sistema genera UN `DICTAMEN_SUFICIENCIA`
 * consolidado al cerrar el consenso de los 3 titulares (PDF + QR + hash). Por
 * eso `documentoDictamenId` ya no se acepta — ningún PDF se sube por miembro.
 */
export const dictamenBorradorSchema = z.object({
  resultado: resultadoRevisionBorradorSchema,
  observaciones: z.string().max(4000).optional(),
});
export type DictamenBorradorInput = z.infer<typeof dictamenBorradorSchema>;

// ============================================================================
// Subsanación de borrador observado (estudiante)
// ============================================================================

/**
 * Input de subsanación del borrador observado (estudiante).
 *
 * **ADR 015 — Bloque 7**: el campo `documentoConformidadAsesorId` se eliminó.
 * Mismo patrón que `corregirProyectoSchema`: el asesor renueva la conformidad
 * por separado (estado `BORRADOR_OBSERVADO`); el service verifica que existe
 * una conformidad `VIGENTE` posterior a la última observación del borrador.
 */
export const subsanarBorradorSchema = z.object({
  /** Nueva versión del BORRADOR_TESIS. */
  documentoBorradorId: uuidSchema,
});
export type SubsanarBorradorInput = z.infer<typeof subsanarBorradorSchema>;

// ============================================================================
// Antiplagio
// ============================================================================

export const registrarReporteAntiplagioSchema = z.object({
  documentoAnalizadoId: uuidSchema,
  archivoReporteId: uuidSchema,
  herramientaUsada: z.string().min(1).max(50),
  porcentajeSimilitud: z.number().min(0).max(100),
  umbralAplicado: z.number().min(0).max(100),
});
export type RegistrarReporteAntiplagioInput = z.infer<typeof registrarReporteAntiplagioSchema>;

// ============================================================================
// Sustentación
// ============================================================================

export const solicitarFechaSustentacionSchema = z.object({
  fechaSugerida: z.coerce.date().optional(),
  observaciones: z.string().max(500).optional(),
});
export type SolicitarFechaSustentacionInput = z.infer<typeof solicitarFechaSustentacionSchema>;

export const programarSustentacionSchema = z.object({
  fechaProgramada: z.coerce.date(),
  horaProgramada: z.string().regex(/^\d{2}:\d{2}$/, "Formato HH:MM"),
  lugar: z.string().min(1).max(200),
  modalidad: modalidadSustentacionSchema,
  numeroResolucion: z.string().min(1),
});
export type ProgramarSustentacionInput = z.infer<typeof programarSustentacionSchema>;

export const reprogramarSustentacionSchema = z.object({
  fechaProgramada: z.coerce.date(),
  horaProgramada: z.string().regex(/^\d{2}:\d{2}$/, "Formato HH:MM"),
  lugar: z.string().min(1).max(200),
  modalidad: modalidadSustentacionSchema,
  motivo: z.string().min(10).max(1000),
});
export type ReprogramarSustentacionInput = z.infer<typeof reprogramarSustentacionSchema>;

export const iniciarSustentacionSchema = z.object({
  /**
   * Lista de docentes presentes en la sala. Si un titular no asiste, el
   * presidente debe declarar la sustitución usando el accesitario en este
   * mismo payload (el accesitario reemplaza al titular ausente para esta
   * sustentación).
   */
  presentes: z
    .array(
      z.object({
        miembroJuradoId: uuidSchema,
        sustituye: uuidSchema.optional(),
      }),
    )
    .min(3),
});
export type IniciarSustentacionInput = z.infer<typeof iniciarSustentacionSchema>;

export const calificarSustentacionSchema = z.object({
  nota: z.number().int().min(0).max(20),
  preguntasFormuladas: z.string().max(4000).optional(),
  observaciones: z.string().max(4000).optional(),
});
export type CalificarSustentacionInput = z.infer<typeof calificarSustentacionSchema>;

/**
 * El endpoint de cierre no recibe payload — el cálculo del promedio y
 * el veredicto se hace en el service a partir de las calificaciones
 * persistidas. Se documenta como objeto vacío para mantener uniformidad.
 */
export const cerrarSustentacionSchema = z.object({}).optional();
export type CerrarSustentacionInput = z.infer<typeof cerrarSustentacionSchema>;

// ============================================================================
// Subsanación de antiplagio observado (estudiante)
// ============================================================================

export const subsanarAntiplagioSchema = z.object({
  /** Comentario del estudiante explicando la corrección (opcional). */
  observaciones: z.string().max(2000).optional(),
});
export type SubsanarAntiplagioInput = z.infer<typeof subsanarAntiplagioSchema>;

// ============================================================================
// Repositorio institucional (Fase 4)
// ============================================================================

export const registrarRepositorioSchema = z.object({
  /** ID del documento TESIS_FINAL subido al expediente. */
  documentoFinalId: uuidSchema,
  /** Resumen ejecutivo / abstract de la tesis (opcional pero recomendado). */
  resumen: z.string().min(50).max(4000).optional(),
  /** Lista de palabras clave para indexación. */
  palabrasClave: z.array(z.string().min(2).max(80)).min(1).max(20),
  /** Si la tesis es pública o de acceso restringido. */
  publicado: z.boolean().default(true),
});
export type RegistrarRepositorioInput = z.infer<typeof registrarRepositorioSchema>;

// ============================================================================
// Trámite del título profesional (Fase 4)
// ============================================================================

/** Sólo el decanato lo invoca — no requiere payload. */
export const iniciarTramiteTituloSchema = z.object({}).optional();
export type IniciarTramiteTituloInput = z.infer<typeof iniciarTramiteTituloSchema>;

export const actualizarTramiteTituloSchema = z.object({
  /**
   * Nuevos estados permitidos desde el endpoint PATCH. `OTORGADO` queda
   * deliberadamente excluido — sólo lo alcanza el endpoint
   * `otorgar-titulo` (que además emite la resolución decanal).
   */
  estado: z.enum(["EN_REVISION", "OBSERVADO", "APROBADO"]),
  observaciones: z.string().max(2000).optional(),
});
export type ActualizarTramiteTituloInput = z.infer<typeof actualizarTramiteTituloSchema>;

export const otorgarTituloSchema = z.object({
  numeroResolucion: z.string().min(1),
});
export type OtorgarTituloInput = z.infer<typeof otorgarTituloSchema>;

// ============================================================================
// Reportes (Fase 5)
// ============================================================================

/**
 * Filtros comunes para los reportes. Todos opcionales — sin filtros
 * devuelve el dataset completo. El service aplica filtrado adicional
 * según rol cuando hace falta (ej. asesor solo ve su carga).
 */
export const filtrosReporteSchema = z.object({
  escuelaId: uuidSchema.optional(),
  desde: z.coerce.date().optional(),
  hasta: z.coerce.date().optional(),
});
export type FiltrosReporteInput = z.infer<typeof filtrosReporteSchema>;

// ============================================================================
// Feriados — admin del calendario (Fase 5 §F5-010)
// ============================================================================

const tipoFeriadoSchema = z.enum(["NACIONAL", "INSTITUCIONAL", "SUSPENSION_ACADEMICA"]);

export const crearFeriadoSchema = z.object({
  fecha: z.coerce.date(),
  nombre: z.string().min(1).max(120),
  tipo: tipoFeriadoSchema,
  descripcion: z.string().max(500).optional(),
});
export type CrearFeriadoInput = z.infer<typeof crearFeriadoSchema>;

// ============================================================================
// Documentos
// ============================================================================

export const subirDocumentoMetaSchema = z.object({
  expedienteId: uuidSchema,
  tipoDocumento: tipoDocumentoSchema,
  versionAnteriorId: uuidSchema.optional(),
  comentario: z.string().max(500).optional(),
});
export type SubirDocumentoMetaInput = z.infer<typeof subirDocumentoMetaSchema>;

// ============================================================================
// Editor LaTeX (Fase C — ADR 010-013)
// ============================================================================

/**
 * Tipos de esquema de proyecto disponibles en el catálogo de plantillas.
 * Espejo del enum `TipoEsquemaProyecto` de Prisma. Se mantiene aquí para
 * que el frontend pueda filtrar el selector de plantillas por tipo sin
 * importar tipos de Prisma client.
 */
export const tipoEsquemaProyectoSchema = z.enum(["CUANTITATIVA", "CUALITATIVA", "MIXTA", "OTRO"]);
export type TipoEsquemaProyecto = z.infer<typeof tipoEsquemaProyectoSchema>;

/**
 * Modalidad del documento que cubre la plantilla. Espejo del enum
 * `ModalidadDocumento` de Prisma. `PROYECTO` corresponde a la etapa de
 * elaboración del proyecto de tesis; `INFORME` a la del informe final. El
 * frontend filtra el selector de plantillas por modalidad según la etapa del
 * expediente (en "Elaboración del proyecto" solo deben verse plantillas
 * PROYECTO).
 */
export const modalidadDocumentoSchema = z.enum(["PROYECTO", "INFORME"]);
export type ModalidadDocumento = z.infer<typeof modalidadDocumentoSchema>;

/**
 * Tipo de archivo dentro del árbol del proyecto. Discriminador del storage
 * híbrido (ADR 011): TEXTO se persiste en la columna `contenidoTexto` de
 * Postgres; BINARIO en MinIO referenciado por `storageKey`.
 */
export const tipoArchivoLatexSchema = z.enum(["TEXTO", "BINARIO"]);
export type TipoArchivoLatex = z.infer<typeof tipoArchivoLatexSchema>;

/**
 * Estados posibles de una compilación LaTeX. El cliente del editor usa este
 * enum para renderizar badge y decidir si habilitar la descarga del PDF.
 */
export const estadoCompilacionSchema = z.enum([
  "PENDIENTE",
  "EN_PROGRESO",
  "EXITOSA",
  "CON_ERRORES",
  "TIMEOUT",
  "ERROR_INTERNO",
]);
export type EstadoCompilacion = z.infer<typeof estadoCompilacionSchema>;

/**
 * Query string para listar plantillas del catálogo. Permite filtrar por
 * tipo de esquema (CUANTITATIVA/…) y por modalidad de documento
 * (PROYECTO/INFORME). El selector de la etapa "Elaboración del proyecto"
 * filtra por `modalidadDocumento=PROYECTO` para no ofrecer plantillas de
 * informe fuera de su etapa.
 */
export const listarPlantillasLatexQuerySchema = z.object({
  tipoEsquema: tipoEsquemaProyectoSchema.optional(),
  modalidadDocumento: modalidadDocumentoSchema.optional(),
});
export type ListarPlantillasLatexQueryInput = z.infer<typeof listarPlantillasLatexQuerySchema>;

/**
 * Body del endpoint `POST /proyectos-latex`. El estudiante elige sobre
 * qué expediente crear el proyecto digital y qué plantilla del catálogo
 * usar como semilla.
 */
export const crearProyectoLatexSchema = z.object({
  expedienteId: uuidSchema,
  plantillaId: uuidSchema,
});
export type CrearProyectoLatexInput = z.infer<typeof crearProyectoLatexSchema>;

// ----------------------------------------------------------------------------
// Editor LaTeX — CRUD del árbol de archivos del proyecto (Fase C Bloque 3)
// ----------------------------------------------------------------------------

/**
 * Regex de path para archivos del proyecto LaTeX (ADR 013 §5).
 * Replica `validarPathArchivo` del backend pero validada también server-side.
 * No incluye `..` ni `/` inicial; eso lo verifica un .refine() adicional.
 */
const pathArchivoRegex = /^[a-zA-Z0-9_\-./]+$/;

const pathArchivoSchema = z
  .string()
  .min(1, "El path no puede ser vacío")
  .max(200, "El path no puede exceder 200 caracteres")
  .regex(pathArchivoRegex, "Solo se admiten letras, dígitos, _, -, . y /")
  .refine((p) => !p.startsWith("/"), "El path no puede empezar con /")
  .refine((p) => !p.endsWith("/"), "El path debe apuntar a un archivo, no a un directorio")
  .refine(
    (p) => !p.split("/").some((seg) => seg === "" || seg === "." || seg === ".."),
    "El path no puede contener segmentos vacíos, '.' ni '..'",
  );

/**
 * Body del PUT que crea o actualiza un archivo de TEXTO. El `path` viene en
 * la URL (wildcard) y el contenido en el body. Los archivos binarios usan
 * multipart en otro endpoint cuando se implementen.
 */
export const escribirArchivoTextoSchema = z.object({
  contenido: z.string().max(1024 * 1024, "El contenido no puede exceder 1 MB"),
});
export type EscribirArchivoTextoInput = z.infer<typeof escribirArchivoTextoSchema>;

/**
 * Body del POST /move. Renombra o reubica un archivo dentro del árbol.
 * Tanto `from` como `to` se validan con el mismo regex de path.
 */
export const moverArchivoSchema = z
  .object({
    from: pathArchivoSchema,
    to: pathArchivoSchema,
  })
  .refine((v) => v.from !== v.to, "El path de origen y destino no pueden ser iguales");
export type MoverArchivoInput = z.infer<typeof moverArchivoSchema>;

/**
 * Query del endpoint GET /proyectos-latex/:id/compilaciones — paginado
 * estándar del proyecto (pageSize máx 50 para no consumir bandwidth con
 * logs largos).
 */
export const listarCompilacionesQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(50).default(20),
});
export type ListarCompilacionesQueryInput = z.infer<typeof listarCompilacionesQuerySchema>;

// ----------------------------------------------------------------------------
// Editor LaTeX — Modo guiado (Fase C+ Bloque 12 — ADR 014)
// ----------------------------------------------------------------------------

/**
 * Tipo de control de UI para un campo guiado. `text` es input single-line;
 * `textarea` es input multi-línea. Otros tipos (select, number, fecha) podrían
 * sumarse después; por ahora los campos de la plantilla cuantitativa son
 * todos text/textarea.
 */
export const tipoCampoGuiadoSchema = z.enum(["text", "textarea"]);
export type TipoCampoGuiado = z.infer<typeof tipoCampoGuiadoSchema>;

/**
 * Declaración de un campo editable del modo guiado. Mirror del JSON que vive
 * en `campos-guiados.json` de cada plantilla.
 */
export const campoGuiadoDefSchema = z.object({
  /** Nombre del comando LaTeX sin la barra (`titulo`, `autor`, ...). */
  comando: z.string().regex(/^[a-zA-Z]+$/, "El nombre del comando debe ser solo letras"),
  /** Archivo del proyecto donde vive el comando (ej. `main.tex`). */
  archivo: z.string().min(1),
  /** Label visible en el formulario. */
  label: z.string().min(1).max(120),
  /** Tipo de control. */
  tipo: tipoCampoGuiadoSchema,
  /** Texto de ayuda mostrado abajo del input (HTML escapado, no LaTeX). */
  ayuda: z.string().max(500).optional(),
  /** Longitud máxima del valor — `validarValorSeguro` la respeta. */
  maxLength: z.number().int().positive().max(10_000).optional(),
  /** Si es obligatorio (UX hint; el backend no rechaza vacío). */
  obligatorio: z.boolean().default(false),
  /**
   * Fuente del valor del campo:
   *  - `manual` (default): lo escribe el estudiante.
   *  - `asesorDesignado`: lo gestiona el sistema desde la designación oficial.
   *    Se sincroniza al crear el proyecto y se renderiza read-only — el
   *    estudiante no lo edita (evita divergencia con la designación del Decanato).
   */
  fuente: z.enum(["manual", "asesorDesignado"]).default("manual"),
  /**
   * Destino de sincronización del valor hacia el sistema (campo → modelo).
   * Simétrico a `fuente` (que es modelo → campo). Si está presente, al guardar
   * el campo se propaga su valor al modelo indicado, en la misma transacción
   * que la escritura del `.tex` (ADR 030):
   *  - `tituloExpediente`: actualiza `ExpedienteTesis.tituloPropuesto`, para que
   *    el título vigente del proyecto sea fuente única en dashboards/documentos.
   * Ausente = el campo no se propaga a ningún modelo.
   */
  sincroniza: z.enum(["tituloExpediente"]).optional(),
  /**
   * Fuente del sistema con la que **pre-rellenar** (hidratar) el campo **al
   * crear** el proyecto, para que la portada salga con datos reales y no con el
   * placeholder de la plantilla. A diferencia de `fuente`, el campo **queda
   * editable** después (el estudiante puede ajustarlo); solo se siembra el
   * valor inicial una vez. Ausente = se deja el default de la plantilla.
   *  - `tituloExpediente`: `ExpedienteTesis.tituloPropuesto`.
   *  - `estudianteNombre`: "Apellidos, Nombres" del estudiante dueño.
   *  - `estudianteCodigo`: `PerfilEstudiante.codigoEstudiante`.
   *  - `escuelaNombre` / `facultadNombre`: de la escuela del expediente.
   */
  hidratacion: z
    .enum([
      "tituloExpediente",
      "estudianteNombre",
      "estudianteCodigo",
      "escuelaNombre",
      "facultadNombre",
    ])
    .optional(),
});
export type CampoGuiadoDef = z.infer<typeof campoGuiadoDefSchema>;

/**
 * Documento completo del archivo `campos-guiados.json`. El seeder lo lee y
 * lo persiste en `PlantillaProyectoLatex.camposGuiadosJson`.
 */
export const camposGuiadosDocSchema = z.object({
  $schema: z.string().optional(),
  version: z.literal(1),
  campos: z.array(campoGuiadoDefSchema).min(1),
});
export type CamposGuiadosDoc = z.infer<typeof camposGuiadosDocSchema>;

/**
 * Body del PUT `/proyectos-latex/:id/guiado/campos/:comando` — el frontend
 * envía solo el valor nuevo. La metadata (archivo, tipo) viene de la plantilla.
 */
export const actualizarCampoGuiadoSchema = z.object({
  valor: z.string().max(10_000),
});
export type ActualizarCampoGuiadoInput = z.infer<typeof actualizarCampoGuiadoSchema>;

/**
 * Una sección del proyecto que el estudiante todavía NO trabajó: su contenido
 * sigue byte-a-byte idéntico al de la plantilla original. Se deriva por
 * comparación con la plantilla (no por strings hardcodeados), y alimenta el
 * aviso suave de completitud antes de enviar a revisión del asesor. El aviso
 * NO bloquea el envío (el loop estudiante↔asesor es ilimitado — ADR 019).
 */
export const seccionPendienteSchema = z.object({
  /** Path del archivo dentro del proyecto. Ej. "secciones/02-marco-teorico.tex". */
  archivo: z.string(),
  /** Título legible (primer encabezado del archivo). Ej. "Marco teórico". */
  titulo: z.string(),
});
export type SeccionPendiente = z.infer<typeof seccionPendienteSchema>;

/** Resumen de completitud del proyecto para el aviso previo al envío. */
export const completitudProyectoSchema = z.object({
  seccionesSinTrabajar: z.array(seccionPendienteSchema),
});
export type CompletitudProyecto = z.infer<typeof completitudProyectoSchema>;

/**
 * Niveles jerárquicos del outline LaTeX. Mirror del enum
 * `NIVELES_OUTLINE` del backend, expuesto acá para que el frontend
 * pueda tipar la navegación del árbol sin importar del api.
 */
export const nivelOutlineSchema = z.enum(["chapter", "section", "subsection", "subsubsection"]);
export type NivelOutline = z.infer<typeof nivelOutlineSchema>;

/**
 * Body del PUT `/proyectos-latex/:id/guiado/seccion/:archivo` — actualiza
 * el título y/o el cuerpo de una sección identificada por
 * `(nivel, ocurrenciaIdx)`. Al menos uno de los dos campos debe venir.
 */
export const actualizarSeccionSchema = z
  .object({
    nivel: nivelOutlineSchema,
    ocurrenciaIdx: z.number().int().min(0),
    nuevoTitulo: z.string().max(500).optional(),
    nuevoCuerpo: z.string().max(200_000).optional(),
  })
  .refine(
    (v) => v.nuevoTitulo !== undefined || v.nuevoCuerpo !== undefined,
    "Debes proveer nuevoTitulo o nuevoCuerpo",
  );
export type ActualizarSeccionInput = z.infer<typeof actualizarSeccionSchema>;

/**
 * Body del POST `/proyectos-latex/:id/guiado/seccion?archivo=...` — RT104.
 *
 * Crea una nueva subsección debajo de un nodo padre identificado por
 * `(padre.nivel, padre.ocurrenciaIdx)`. El backend inserta
 * `\<nivelNuevo>{tituloInicial}% [USER]` al final del cuerpo del padre
 * (después de hijos previos, antes del próximo hermano del padre), y
 * devuelve el outline actualizado.
 *
 * Reglas que el backend re-valida (este schema solo cubre forma):
 *  - `nivelNuevo` debe ser EXACTAMENTE uno más profundo que el padre
 *    (section → subsection, subsection → subsubsection). El backend
 *    rechaza saltarse niveles.
 *  - `chapter` no es válido aquí: los capítulos se gestionan a nivel
 *    archivo, no se crean inline desde el editor. El refine lo bloquea
 *    explícitamente para devolver un mensaje claro al cliente.
 */
export const crearSubseccionSchema = z.object({
  padre: z.object({
    nivel: nivelOutlineSchema,
    ocurrenciaIdx: z.number().int().min(0),
  }),
  nivelNuevo: nivelOutlineSchema.refine(
    (n) => n !== "chapter",
    "No se puede crear un chapter inline desde el editor — gestioná los capítulos a nivel archivo",
  ),
  tituloInicial: z
    .string()
    .trim()
    .min(1, "El título inicial no puede estar vacío")
    .max(500, "El título inicial excede 500 caracteres"),
});
export type CrearSubseccionInput = z.infer<typeof crearSubseccionSchema>;

// ----------------------------------------------------------------------------
// Bibliografía BibTeX CRUD (Fase C+ Bloque 13)
// ----------------------------------------------------------------------------

/**
 * Tipos BibTeX soportados por el editor guiado. Subset de los más usados en
 * tesis. El backend acepta cualquiera; el formulario del frontend muestra
 * campos discriminados para los 4 listados acá.
 */
export const bibTipoSchema = z.enum([
  "article",
  "book",
  "inproceedings",
  "incollection",
  "phdthesis",
  "mastersthesis",
  "misc",
  "online",
]);
export type BibTipo = z.infer<typeof bibTipoSchema>;

/** Regex para keys BibTeX: alfanuméricos + guion + underscore + dos puntos. */
const BIB_KEY_REGEX = /^[a-zA-Z0-9_:-]+$/;

const bibEntryInputSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(80)
    .regex(BIB_KEY_REGEX, "La key solo puede tener letras, dígitos, _, - y :"),
  type: bibTipoSchema,
  /** Map de campos. Llaves en minúsculas, valores arbitrarios (max 2000 chars c/u). */
  fields: z.record(z.string().min(1), z.string().max(2000)),
});

export const crearBibEntrySchema = bibEntryInputSchema;
export type CrearBibEntryInput = z.infer<typeof crearBibEntrySchema>;

/**
 * Body del PUT — la key no puede cambiar (el path la identifica). Si el
 * usuario quiere renombrar, debe DELETE + POST.
 */
export const actualizarBibEntrySchema = bibEntryInputSchema.omit({ key: true });
export type ActualizarBibEntryInput = z.infer<typeof actualizarBibEntrySchema>;
